// frontend/src/utils/analysisMarkers.js
//
// Builds the entry/TP/SL chart markers from a backend trading-analysis result.
// Pure function of the analysis result — no chart or React dependencies — so it
// can be unit-tested and reused independently of MultiPaneChart.

/**
 * @param {object} analysisResult - response from /api/analysis
 * @returns {Array|null} markers sorted by time, or null when the result carries
 *   no position data (caller should clear existing markers in that case).
 */
export function buildAnalysisMarkers(analysisResult) {
  const positionData = analysisResult?.debug?.superTrendRaw?.positionData;
  if (!positionData) return null;

  // Map graded signals by their timestamp for easy lookup
  const gradedSignalsByTime = new Map();
  (analysisResult.allSignals || []).forEach(signal => {
    gradedSignalsByTime.set(signal.baseSignal.time, signal);
  });

  // Map confirmed positions by their ID, attaching the corresponding graded signal
  const positionsById = new Map();
  if (positionData.positions) {
    positionData.positions.forEach(pos => {
      positionsById.set(pos.id, {
        ...pos,
        gradedSignal: gradedSignalsByTime.get(pos.entryTime)
      });
    });
  }

  const entryMarkers = [];
  const exitMarkers = [];

  // Entry markers from the confirmed positions list
  positionsById.forEach(pos => {
    if (!pos.gradedSignal) return; // Skip if there's no corresponding signal grade

    const signal = pos.gradedSignal;
    const base = signal.baseSignal;
    let color = '#9E9E9E', shape = 'diamond', position = base.direction === 'BUY' ? 'belowBar' : 'aboveBar', text = `[${signal.strengthGrade}]`, size = 1;

    switch (signal.strengthGrade) {
      case 'A+': case 'A':
        color = base.direction === 'BUY' ? '#28A745' : '#DC3545';
        shape = base.direction === 'BUY' ? 'arrowUp' : 'arrowDown';
        text = `${base.direction} (${signal.finalScore})`;
        size = 1;
        break;
      case 'B': case 'C':
        color = base.direction === 'BUY' ? '#4CAF50' : '#F44336';
        shape = 'circle';
        text = signal.strengthGrade;
        size = 1;
        break;
      default:
        shape = 'cross'; text = signal.strengthGrade; size = 0; break;
    }

    // Churn-gated entries: the flip is NOT an immediate entry. The flip bar
    // shows the wait state; a confirmed plan adds the real ENTER marker at the
    // confirmation bar (this is where the indicator "provides the entry").
    const plan = signal.entryPlan;
    if (plan && plan.mode === 'WAIT_CONFIRM') {
      if (plan.status === 'PENDING') {
        color = '#f59e0b';
        shape = 'circle';
        text = `WAIT >${plan.confirmLevel}`;
        size = 1;
      } else if (plan.status === 'EXPIRED') {
        color = '#9CA3AF';
        shape = 'cross';
        text = 'no confirm';
        size = 0;
      } else if (plan.status === 'CONFIRMED') {
        color = '#9CA3AF';
        shape = 'circle';
        text = `wait ${signal.strengthGrade}`;
        size = 0;
        entryMarkers.push({
          time: plan.confirmedAt,
          position,
          color: base.direction === 'BUY' ? '#28A745' : '#DC3545',
          shape: base.direction === 'BUY' ? 'arrowUp' : 'arrowDown',
          text: `ENTER (${signal.finalScore})`,
          size: 1,
          id: `confirm_${plan.confirmedAt}`,
          data: { ...signal }
        });
      }
    }

    entryMarkers.push({
      time: base.time, position, color, shape, text, size,
      id: `signal_${base.time}`,
      data: { ...signal }
    });
  });

  // Exit markers, linked to their position by positionId
  // A. Take Profit markers
  if (positionData.tpSignals) {
    positionData.tpSignals.forEach((tp, index) => {
      const associatedPosition = positionsById.get(tp.positionId);
      if (!associatedPosition || !associatedPosition.gradedSignal) return;
      // Gated entry that never confirmed => we said "no entry taken", so its
      // hypothetical TP exits must not render either (they'd contradict it).
      if (associatedPosition.gradedSignal.entryPlan?.status === 'EXPIRED') return;

      const gradeInfo = associatedPosition.gradedSignal;

      exitMarkers.push({
        time: tp.time,
        position: associatedPosition.direction === 'BUY' ? 'aboveBar' : 'belowBar',
        color: tp.type === 'TP1' ? '#4caf50' : tp.type === 'TP2' ? '#ff9800' : '#f44336',
        shape: 'circle',
        text: tp.type,
        size: 0,
        id: `tp_${tp.time}_${index}`,
        data: {
          baseSignal: {
            price: associatedPosition.entryPrice,
            direction: associatedPosition.direction,
            time: associatedPosition.entryTime
          },
          strengthGrade: gradeInfo.strengthGrade,
          finalScore: gradeInfo.finalScore,
          regimeContext: gradeInfo.regimeContext,
          confirmations: gradeInfo.confirmations,
          warnings: gradeInfo.warnings,
          exitType: tp.type,
          exitPrice: tp.price,
          exitReason: tp.reason || 'Dynamic TP condition met',
          partial: tp.partial ?? null,
          execution: tp.execution || null
        }
      });
    });
  }

  // B. Stop Loss markers
  if (positionData.stopSignals) {
    positionData.stopSignals.forEach((sl, index) => {
      const associatedPosition = positionsById.get(sl.positionId);
      if (!associatedPosition || !associatedPosition.gradedSignal) return;
      if (associatedPosition.gradedSignal.entryPlan?.status === 'EXPIRED') return;

      const gradeInfo = associatedPosition.gradedSignal;

      exitMarkers.push({
        time: sl.time,
        position: associatedPosition.direction === 'BUY' ? 'belowBar' : 'aboveBar',
        color: '#D32F2F',
        shape: 'cross',
        text: 'SL',
        size: 1,
        id: `sl_${sl.time}_${index}`,
        data: {
          baseSignal: {
            price: associatedPosition.entryPrice,
            direction: associatedPosition.direction,
            time: associatedPosition.entryTime
          },
          strengthGrade: gradeInfo.strengthGrade,
          finalScore: gradeInfo.finalScore,
          regimeContext: gradeInfo.regimeContext,
          confirmations: gradeInfo.confirmations,
          warnings: gradeInfo.warnings,
          exitType: 'Stop Loss',
          exitPrice: sl.price,
          execution: sl.execution || null
        }
      });
    });
  }

  const allMarkers = [...entryMarkers, ...exitMarkers];
  allMarkers.sort((a, b) => a.time - b.time);
  return allMarkers;
}
